// src/services/analytics/index.ts
//
// 10章のプライバシー原則: 個人を特定できる情報(映像・骨格データ・氏名・メール等)は
// 一切送信しない。送信するのはUI操作の集計に使う匿名イベント名のみであり、
// 送信先の可否(オプトアウト)は設定画面からいつでも切り替えられる。

export type AnalyticsEventName =
  | "app_opened"
  | "onboarding_completed"
  | "measurement_started"
  | "measurement_completed"
  | "xray_mode_toggled"
  | "pdf_exported"
  | "settings_opened"
  | "data_deleted";

interface AnalyticsEvent {
  name: AnalyticsEventName;
  timestamp: number;
  // 数値・enum程度の非個人情報プロパティのみ許可(自由文字列のvalueは禁止)
  properties?: Record<string, string | number | boolean>;
}

const STORAGE_KEY = "kinemalens_analytics_opt_out";

export function isAnalyticsOptedOut(): boolean {
  return localStorage.getItem(STORAGE_KEY) === "true";
}

export function setAnalyticsOptOut(optOut: boolean): void {
  localStorage.setItem(STORAGE_KEY, String(optOut));
}

const queue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

export function track(
  name: AnalyticsEventName,
  properties?: AnalyticsEvent["properties"],
): void {
  if (isAnalyticsOptedOut()) return;
  queue.push({ name, timestamp: Date.now(), properties });
  scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, 5000);
}

async function flush(): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
  if (!endpoint) return; // エンドポイント未設定時はローカルログのみ(開発時)
  try {
    await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
  } catch {
    // 送信失敗しても計測フローには一切影響させない(アナリティクスはベストエフォート)
  }
}
